import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
// Импорты для MAX_FRAMELESS_WIDTH_M и MAX_FRAMELESS_HEIGHT_M больше не нужны

const FramelessCreationModal = ({ isOpen, onClose, onCreate, initialPosition }) => {
    const [dimensions, setDimensions] = useState({ width: 6, length: 4, height: 3 });
    const [validation, setValidation] = useState({ isValid: true, error: '', suggestions: [] });

    useEffect(() => {
        if (isOpen) {
            const initialDims = { width: 6, length: 4, height: 3 };
            setDimensions(initialDims);
            validate();
        }
    }, [isOpen]);

    const validate = () => {
        // Проверка перегородок отключена в безкаркасном режиме
        setValidation({ isValid: true, error: '', suggestions: [] });
    };

    const handleChange = (e) => {
        const newDimensions = {
            ...dimensions,
            [e.target.name]: parseFloat(e.target.value) || 0,
        };
        setDimensions(newDimensions);
        validate();
    };

    const handleCreate = () => {
        onCreate(dimensions, initialPosition, validation.suggestions);
        onClose();
    };

    const previewScale = useMemo(() => {
        const previewBoxSize = 180;
        if (dimensions.width === 0 || dimensions.length === 0) return 1;
        return Math.min(previewBoxSize / dimensions.width, previewBoxSize / dimensions.length);
    }, [dimensions]);

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
    };

    return (
        <div className={`fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <motion.div
                variants={modalVariants}
                initial="hidden"
                animate={isOpen ? "visible" : "hidden"}
                className="bg-white rounded-xl shadow-2xl w-full max-w-lg border border-gray-200"
            >
                <div className="p-5 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-800">Створити нову будівлю</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full"><XMarkIcon className="w-6 h-6" /></button>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Ширина (м)</label>
                            <input type="number" name="width" value={dimensions.width} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" step="0.1" min="1" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Довжина (м)</label>
                            <input type="number" name="length" value={dimensions.length} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" step="0.1" min="1" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Висота (м)</label>
                            <input type="number" name="height" value={dimensions.height} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" step="0.1" min="2" />
                        </div>
                    </div>
                    <div className="flex flex-col items-center justify-center bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4">
                        <svg width="200" height="200" viewBox="-110 -110 220 220">
                            <rect
                                x={-dimensions.width * previewScale / 2}
                                y={-dimensions.length * previewScale / 2}
                                width={dimensions.width * previewScale}
                                height={dimensions.length * previewScale}
                                fill={validation.isValid ? "rgba(74, 222, 128, 0.2)" : "rgba(248, 113, 113, 0.2)"}
                                stroke={validation.isValid ? "rgba(22, 163, 74, 0.5)" : "rgba(239, 68, 68, 0.5)"}
                                strokeWidth="2"
                            />
                            {validation.suggestions.map(sug => (
                                <line
                                    key={sug.id}
                                    x1={sug.start.x * previewScale} y1={sug.start.y * previewScale}
                                    x2={sug.end.x * previewScale} y2={sug.end.y * previewScale}
                                    stroke="rgba(202, 138, 4, 0.8)"
                                    strokeWidth="2"
                                    strokeDasharray="4 4"
                                />
                            ))}
                        </svg>
                    </div>
                </div>
                {validation.error && <p className="text-sm text-yellow-700 text-center px-6 pb-4">{validation.error}</p>}

                <div className="p-5 bg-gray-50 rounded-b-xl flex justify-end">
                    <button onClick={onClose} className="px-5 py-2.5 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-100 mr-3">Скасувати</button>
                    <button onClick={handleCreate} className="px-5 py-2.5 bg-primary-blue text-white rounded-md shadow-sm text-sm font-semibold hover:bg-hover-blue">Створити</button>
                </div>
            </motion.div>
        </div>
    );
};

export default FramelessCreationModal;